
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Search, RefreshCw, UserPlus } from "lucide-react";
import { UserRole } from "@/types/roles";
import { CustomerTable } from "./customers/CustomerTable";
import { EditUserDialog } from "./customers/EditUserDialog";
import { UserInviteDialog } from "./customers/UserInviteDialog";
import { useCustomers } from "@/hooks/useCustomers";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export const UserManagement = () => {
  const { 
    customers, 
    loading, 
    searchQuery, 
    setSearchQuery, 
    loadCustomers, 
    updateUserRole,
    approveUser,
    deleteUser,
    getFilteredCustomers
  } = useCustomers();
  
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [inviteUserDialogOpen, setInviteUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [newUserRole, setNewUserRole] = useState<UserRole>("user");

  // Load customers on component mount and set up subscription
  useEffect(() => {
    console.log("UserManagement mounted, loading customers...");
    loadCustomers();
    
    // Listen for auth changes that might affect our user list
    const { data: authListener } = supabase.auth.onAuthStateChange((event) => {
      console.log("Auth state changed:", event);
      loadCustomers();
    });
    
    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, [loadCustomers]);

  const handleEditUser = (user: any) => {
    if (user) {
      console.log("Editing user:", user);
      setSelectedUser(user);
      setNewUserRole(user.role || 'user');
      setEditUserDialogOpen(true);
    } else {
      toast.error("Benutzer nicht gefunden");
    }
  };

  const handleSaveUserRole = async () => {
    if (selectedUser) {
      console.log(`Saving role ${newUserRole} for user ${selectedUser.id}`);
      const success = await updateUserRole(selectedUser.id, newUserRole);
      if (success) {
        setEditUserDialogOpen(false);
        setSelectedUser(null);
        toast.success(`Benutzerrolle erfolgreich auf ${newUserRole} aktualisiert`);
      }
    }
  };

  const handleApproveUser = async (user: any) => {
    if (user) {
      console.log("Approving user:", user);
      const success = await approveUser(user.id);
      if (success) {
        console.log("User approved successfully");
      }
    }
  };

  const handleDeleteUser = async (user: any) => {
    if (user) {
      console.log("Deleting user:", user);
      const success = await deleteUser(user.id);
      if (success) {
        console.log("User deleted successfully");
      }
    }
  };

  const handleRefreshData = () => {
    toast.info("Benutzerdaten werden aktualisiert...");
    loadCustomers();
  };

  return (
    <div className="space-y-6">
      <Card className="backdrop-blur-sm bg-white/10 border border-gray-700/30">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-xl font-semibold text-white">Benutzerverwaltung</CardTitle>
            <CardDescription className="text-gray-400">
              Verwalten Sie alle Benutzer und deren Zugänge
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              onClick={() => setInviteUserDialogOpen(true)}
              className="bg-[#41c7f0] text-white hover:bg-[#3ab1d9]"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Benutzer einladen
            </Button>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input 
                placeholder="Suchen..." 
                className="pl-10 w-64 bg-[#1A2433] border-gray-700 text-white" 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Button 
              variant="outline" 
              onClick={handleRefreshData} 
              className="h-10 w-10 p-0 border-gray-700 text-gray-300 bg-[#1A2433]"
              title="Daten aktualisieren"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="all">Alle Benutzer</TabsTrigger>
              <TabsTrigger value="active">Aktive Benutzer</TabsTrigger>
              <TabsTrigger value="inactive">Inaktive Benutzer</TabsTrigger>
              <TabsTrigger value="pending">Einladungen</TabsTrigger>
              <TabsTrigger value="admins">Administratoren</TabsTrigger>
            </TabsList>
            
            <TabsContent value="all">
              <CustomerTable 
                customers={getFilteredCustomers('all')} 
                loading={loading} 
                onEditUser={handleEditUser}
                onApproveUser={handleApproveUser}
                onDeleteUser={handleDeleteUser}
              />
            </TabsContent>
            
            <TabsContent value="active">
              <CustomerTable 
                customers={getFilteredCustomers('active')} 
                loading={loading} 
                onEditUser={handleEditUser}
                onApproveUser={handleApproveUser}
                onDeleteUser={handleDeleteUser}
              />
            </TabsContent>
            
            <TabsContent value="inactive">
              <CustomerTable 
                customers={getFilteredCustomers('inactive')} 
                loading={loading} 
                onEditUser={handleEditUser}
                onApproveUser={handleApproveUser}
                onDeleteUser={handleDeleteUser}
              />
            </TabsContent>
            
            <TabsContent value="pending">
              <CustomerTable 
                customers={getFilteredCustomers('pending')} 
                loading={loading} 
                onEditUser={handleEditUser}
                onApproveUser={handleApproveUser}
                onDeleteUser={handleDeleteUser}
              />
            </TabsContent>
            
            <TabsContent value="admins">
              <CustomerTable 
                customers={getFilteredCustomers('admins')} 
                loading={loading} 
                onEditUser={handleEditUser}
                onApproveUser={handleApproveUser}
                onDeleteUser={handleDeleteUser}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <EditUserDialog
        open={editUserDialogOpen}
        onOpenChange={setEditUserDialogOpen}
        selectedUser={selectedUser}
        newUserRole={newUserRole}
        onRoleChange={setNewUserRole}
        onSave={handleSaveUserRole}
      />
      
      <UserInviteDialog 
        open={inviteUserDialogOpen}
        onOpenChange={setInviteUserDialogOpen}
        onInvited={loadCustomers}
      />
    </div>
  );
};
